import { VendorReportData, PortfolioReportData, PRIORITY, gradeOf, clean } from './reportPdf';

/** Spreadsheet-safe cell: quoted, and a leading = + - @ is neutralised so a cell can't run as a formula. */
const cell = (v: unknown) => {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@\t\r]/.test(s) && !/^-?\d+(\.\d+)?$/.test(s)) s = `'${s}`;
  return `"${s.replace(/"/g, '""')}"`;
};
const toCsv = (rows: unknown[][]) => '﻿' + rows.map(r => r.map(cell).join(',')).join('\r\n');

export interface CsvResult { csv: string; rows: string[][]; header: string[] }

export function findingsCsv(data: VendorReportData, minSeverity: 'Low' | 'Medium' | 'High' | 'Critical' = 'Low'): CsvResult {
  const order = ['Critical', 'High', 'Medium', 'Low'];
  const cut = order.indexOf(minSeverity);
  const header = ['Finding ID', 'Vendor', 'Domain', 'Finding', 'Category', 'Severity', 'Score impact', 'Remediation target',
    'Environment', 'Affected assets', 'Observation', 'Recommendation', 'Assessment date', 'Scan ID'];
  const rows = data.findings
    .map((f, i) => ({ f, id: `F-${String(i + 1).padStart(2, '0')}` }))
    .filter(({ f }) => order.indexOf(f.severity) <= cut)
    .map(({ f, id }) => [
      id, data.vendor.name, data.vendor.domain, clean(f.name), f.category, f.severity, String(f.score_impact),
      PRIORITY[f.severity]?.window || '', f.environment || '', (f.endpoints || []).join('; '),
      clean(f.description), clean(f.recommendation), data.scan?.completed_at?.slice(0, 10) || '', data.scan?.id || '',
    ]);
  return { csv: toCsv([header, ...rows]), rows, header };
}

export function vendorRegisterCsv(data: PortfolioReportData): CsvResult {
  const header = ['Vendor', 'Domain', 'Category', 'Owner', 'Lifecycle', 'Data sensitivity', 'Score', 'Grade', 'Risk level',
    'Previous score', 'Change', 'Critical', 'High', 'Medium', 'Low', 'Last assessed', 'Contract end', ...data.categories];
  const rows = [...data.vendors].sort((a, b) => (a.score ?? -1) - (b.score ?? -1)).map(v => [
    v.name, v.domain, v.category || '', v.owner_name || '', v.lifecycle || '', v.data_sensitivity || '',
    v.score == null ? '' : String(v.score), v.score == null ? '' : gradeOf(v.score), v.risk_level || '',
    v.previous_score == null ? '' : String(v.previous_score),
    v.score != null && v.previous_score != null ? String(v.score - v.previous_score) : '',
    String(v.counts?.critical || 0), String(v.counts?.high || 0), String(v.counts?.medium || 0), String(v.counts?.low || 0),
    v.scan_date?.slice(0, 10) || '', v.contract_end_date?.slice(0, 10) || '',
    ...data.categories.map(c => (typeof v.category_scores?.[c] === 'number' ? String(Math.round(v.category_scores[c])) : '')),
  ]);
  return { csv: toCsv([header, ...rows]), rows, header };
}

export function downloadFile(content: Blob | string, filename: string, type = 'text/csv;charset=utf-8') {
  const blob = typeof content === 'string' ? new Blob([content], { type }) : content;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'report';
